import React from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { TourStop } from '../../types/deliveryTour';
import { StopCard } from './StopCard';

interface SortableStopListProps {
  stops: TourStop[];
  canReorder: boolean;
  onOpen: (stop: TourStop) => void;
  onReorder: (ids: string[]) => void;
}

export const SortableStopList: React.FC<SortableStopListProps> = ({
  stops,
  canReorder,
  onOpen,
  onReorder,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 400, tolerance: 8 },
    })
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = stops.map((s) => s.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(ids, oldIndex, newIndex));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={stops.map((s) => s.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-2">
          {stops.map((stop) => (
            <StopCard
              key={stop.id}
              stop={stop}
              sortable={canReorder}
              onOpen={onOpen}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};
